import React, { useState, useEffect, useCallback } from 'react';
import { 
  Star, Settings, ToggleLeft, ToggleRight, Save, RefreshCw, 
  Check, X, Search, Package, Eye, Edit3, Trash2, Plus,
  AlertTriangle, Info, ExternalLink, Award, TrendingUp, Filter
} from 'lucide-react';
import API_URL from '../config/ApiUrl';
import { useTheme } from '../arrangement/ThemeContext';
export const FeaturedProductsConfig = () => {
  const [config, setConfig] = useState({
    useAdminProducts: false,
    productIds: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const { colors } = useTheme(); 
  
  const [searchPage, setSearchPage] = useState(1);
    const [searchLoading, setSearchLoading] = useState(false);
    const [hasMoreResults, setHasMoreResults] = useState(true);
    const [searchFilters, setSearchFilters] = useState({
    brand: '',
    type: '',
    minRating: 0
    });
  const [showFilters, setShowFilters] = useState(false);


    const searchProducts = async (query = '', page = 1, resetResults = true) => {
  if (!query.trim() && page === 1) return;
  
  setSearchLoading(true);
  try {
    const params = new URLSearchParams({
      search: query,
      page: page,
      per_page: 20,
      exclude_selected: selectedProducts.join(','),
      ...(searchFilters.brand && { brand: searchFilters.brand }),
      ...(searchFilters.type && { type: searchFilters.type }),
      ...(searchFilters.minRating && { min_rating: searchFilters.minRating })
    });

    const response = await fetch(`${API_URL}/api/admin/products/search?${params}`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      if (resetResults) {
        setAvailableProducts(data.products || []);
      } else {
        setAvailableProducts(prev => [...prev, ...(data.products || [])]);
      }
      setHasMoreResults(data.has_more || false);
      setSearchPage(page);
    }
  } catch (error) {
    console.error('Search failed:', error);
  } finally {
    setSearchLoading(false);
  }
};

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Debounced search
const debouncedSearch = useCallback(
  debounce((term) => searchProducts(term, 1, true), 300),
  [selectedProducts, searchFilters]
);

// Update search on input change

    useEffect(() => {
    const loadData = async () => {
        setLoading(true);
        // Load products first
        await loadAvailableProducts();
        // Then load config (which won't override products anymore)
        await loadConfiguration();
        setLoading(false);
    };
    loadData();
    }, []);

    useEffect(() => {
        debouncedSearch(searchTerm);
    }, [searchTerm, debouncedSearch]);

    const loadConfiguration = async () => {
        try {
            const response = await fetch(`${API_URL}/api/admin/featured-products/config`, {
            credentials: 'include'
        });
        
        if (response.ok) {
            const data = await response.json();
            setConfig(data.data);
            setSelectedProducts(data.data.productIds || []);
        
            // If we have selected products with full details, ADD them to availableProducts
            if (data.data.selectedProducts && data.data.selectedProducts.length > 0) {
                setAvailableProducts(prev => {
                    const existingIds = prev.map(p => p.id);
                    const newProducts = data.data.selectedProducts.filter(p => !existingIds.includes(p.id));
                    return newProducts.length > 0 ? [...prev, ...newProducts] : prev;
                });
            }
        }
    } catch (error) {
        console.error('Failed to load configuration:', error);
        showMessage('error', 'Failed to load configuration');
    }
    };

  const loadAvailableProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/products?per_page=100`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailableProducts(data.products || []);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const saveConfiguration = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/featured-products/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          useAdminProducts: config.useAdminProducts,
          productIds: selectedProducts
        })
      });

      if (response.ok) {
        setConfig({
          useAdminProducts: config.useAdminProducts,
          productIds: selectedProducts
        });
        showMessage('success', 'Configuration saved successfully!');
      } else {
        showMessage('error', 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Failed to save configuration:', error);
      showMessage('error', 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const toggleAdminProducts = () => {
    setConfig(prev => ({
      ...prev,
      useAdminProducts: !prev.useAdminProducts
    }));
  };

  const addProduct = (productId) => {
    if (!selectedProducts.includes(productId) && selectedProducts.length < 5) {
      setSelectedProducts(prev => [...prev, productId]);
    }
  };

  const removeProduct = (productId) => {
    setSelectedProducts(prev => prev.filter(id => id !== productId));
  };

  const moveProduct = (productId, direction) => {
    const currentIndex = selectedProducts.indexOf(productId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex >= 0 && newIndex < selectedProducts.length) {
      const newOrder = [...selectedProducts];
      [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
      setSelectedProducts(newOrder);
    }
  };

  const getProductById = (id) => {
    return availableProducts.find(p => p.id === id);
  };

  const filteredProducts = availableProducts.filter(product => {
    const matchesSearch = !searchTerm || 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.brand.toLowerCase().includes(searchTerm.toLowerCase());
    
    const notSelected = !selectedProducts.includes(product.id);
    
    return matchesSearch && notSelected;
  });

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '4rem',
        color: '#888'
      }}>
        <RefreshCw size={24} style={{ marginRight: '12px', animation: 'spin 1s linear infinite' }} />
        Loading configuration...
      </div>
    );
  }

  return (
    <div style={{
      background: colors.bgTertiary,
      borderRadius: '20px',
      padding: '32px',
      border: '1px solid #333'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px'
      }}>
        <div>
          <h2 style={{
            margin: '0 0 8px 0',
            fontSize: '24px',
            fontWeight: '700',
            color: '#e5e5e5',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Star size={24} style={{ color: '#fbbf24' }} />
            Featured Products Configuration
          </h2>
          <p style={{
            margin: 0,
            color: '#888',
            fontSize: '14px'
          }}>
            Configure which products appear in the featured section of your homepage
          </p>
        </div>
        
        <button
          onClick={loadConfiguration}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: '#333',
            color: '#ccc',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Status Messages */}
      {message.text && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '24px',
          background: message.type === 'success' ? '#10b98120' : '#ef444420',
          border: `1px solid ${message.type === 'success' ? '#10b981' : '#ef4444'}`,
          color: message.type === 'success' ? '#10b981' : '#ef4444',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {message.type === 'success' ? <Check size={16} /> : <AlertTriangle size={16} />}
          {message.text}
        </div>
      )}

      {/* Configuration Toggle */}
      <div style={{
        background: '#0f0f0f',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid #333',
        marginBottom: '32px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px'
        }}>
          <div>
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: '18px',
              fontWeight: '600',
              color: '#e5e5e5'
            }}>
              Featured Products Mode
            </h3>
            <p style={{
              margin: 0,
              color: '#888',
              fontSize: '14px'
            }}>
              Choose between admin-curated products or algorithm-based selection
            </p>
          </div>
          
          <button
            onClick={toggleAdminProducts}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: config.useAdminProducts 
                ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' 
                : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {config.useAdminProducts ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
            {config.useAdminProducts ? 'Admin Curated' : 'Algorithm Based'}
          </button>
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px',
          marginTop: '16px'
        }}>
          {/* Algorithm Mode Info */}
          <div style={{
            padding: '16px',
            background: config.useAdminProducts ? '#333' : '#8b5cf620',
            border: `1px solid ${config.useAdminProducts ? '#444' : '#8b5cf6'}`,
            borderRadius: '12px',
            opacity: config.useAdminProducts ? 0.6 : 1
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px',
              color: config.useAdminProducts ? '#888' : '#8b5cf6'
            }}>
              <TrendingUp size={16} />
              <span style={{ fontSize: '14px', fontWeight: '600' }}>Algorithm Mode</span>
            </div>
            <p style={{
              margin: 0,
              fontSize: '12px',
              color: config.useAdminProducts ? '#666' : '#ccc',
              lineHeight: '1.4'
            }}>
              Automatically shows top 5 highest-rated products with most reviews. Updates dynamically as new reviews come in.
            </p>
          </div>
          
          {/* Admin Mode Info */}
          <div style={{
            padding: '16px',
            background: config.useAdminProducts ? '#fbbf2420' : '#333',
            border: `1px solid ${config.useAdminProducts ? '#fbbf24' : '#444'}`,
            borderRadius: '12px',
            opacity: config.useAdminProducts ? 1 : 0.6
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px',
              color: config.useAdminProducts ? '#fbbf24' : '#888'
            }}>
              <Award size={16} />
              <span style={{ fontSize: '14px', fontWeight: '600' }}>Admin Curated</span>
            </div>
            <p style={{
              margin: 0,
              fontSize: '12px',
              color: config.useAdminProducts ? '#ccc' : '#666',
              lineHeight: '1.4'
            }}>
              Manually select up to 5 specific products to feature. Full control over order and selection.
            </p>
          </div>
        </div>
      </div>

      {/* Admin Product Selection (only show if admin mode is enabled) */}
      {config.useAdminProducts && (
        <div style={{
          background: '#0f0f0f',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid #333',
          marginBottom: '32px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px'
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: '#e5e5e5'
            }}>
              Selected Products ({selectedProducts.length}/5)
            </h3>
            
            <button
              onClick={() => setShowAddModal(true)}
              disabled={selectedProducts.length >= 5}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: selectedProducts.length >= 5 ? '#555' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: selectedProducts.length >= 5 ? 'not-allowed' : 'pointer',
                opacity: selectedProducts.length >= 5 ? 0.6 : 1
              }}
            >
              <Plus size={16} />
              Add Product
            </button>
          </div>

          {/* Selected Products List */}
          {selectedProducts.length > 0 ? (
            <div style={{ display: 'grid', gap: '12px' }}>
              {selectedProducts.map((productId, index) => {
                const product = getProductById(productId);
                if (!product) return null;
                
                return (
                  <div
                    key={productId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px',
                      background: colors.bgTertiary,
                      border: '1px solid #333',
                      borderRadius: '12px'
                    }}
                  >
                    {/* Position Number */}
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: index === 0 ? '#fbbf24' : index === 1 ? '#9ca3af' : index === 2 ? '#b45309' : '#8b5cf6',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: '700'
                    }}>
                      {index + 1}
                    </div>

                    {/* Product Image */}
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '8px',
                      background: '#2a2a2a',
                      border: '1px solid #333',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}>
                      {product.coverImage ? (
                        <img
                          src={product.coverImage.imageUrl}
                          alt={product.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <Package size={20} style={{ color: '#666' }} />
                      )}
                    </div>

                    {/* Product Info */}
                    <div style={{ flex: 1 }}>
                      <h4 style={{
                        margin: '0 0 4px 0',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#e5e5e5'
                      }}>
                        {product.name}
                      </h4>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '12px',
                        color: '#888'
                      }}>
                        <span>{product.brand}</span>
                        <span>•</span>
                        <span style={{ textTransform: 'capitalize' }}>{product.type}</span>
                        {product.averageRating > 0 && (
                          <>
                            <span>•</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Star size={12} style={{ color: '#fbbf24' }} />
                              <span>{product.averageRating} ({product.reviewCount})</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        onClick={() => moveProduct(productId, 'up')}
                        disabled={index === 0}
                        style={{
                          padding: '6px',
                          background: index === 0 ? '#333' : '#555',
                          color: index === 0 ? '#666' : '#ccc',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: index === 0 ? 'not-allowed' : 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        ↑
                      </button>
                      
                      <button
                        onClick={() => moveProduct(productId, 'down')}
                        disabled={index === selectedProducts.length - 1}
                        style={{
                          padding: '6px',
                          background: index === selectedProducts.length - 1 ? '#333' : '#555',
                          color: index === selectedProducts.length - 1 ? '#666' : '#ccc',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: index === selectedProducts.length - 1 ? 'not-allowed' : 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        ↓
                      </button>
                      
                      <button
                        onClick={() => removeProduct(productId)}
                        style={{
                          padding: '6px 8px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '32px',
              color: '#888',
              background: '#333',
              borderRadius: '12px',
              border: '2px dashed #555'
            }}>
              <Package size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p style={{ margin: 0, fontSize: '14px' }}>
                No products selected. Click "Add Product" to choose featured products.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Save Button */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '24px',
        borderTop: '1px solid #333'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#888',
          fontSize: '14px'
        }}>
          <Info size={16} />
          Changes will be reflected on the homepage immediately after saving
        </div>
        
        <button
          onClick={saveConfiguration}
          disabled={saving}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: saving ? '#555' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: saving ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          {saving ? (
            <>
              <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
              Saving...
            </>
          ) : (
            <>
              <Save size={16} />
              Save Configuration
            </>
          )}
        </button>
      </div>

      {/* Add Product Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: colors.bgTertiary,
            borderRadius: '20px',
            padding: '32px',
            border: '1px solid #333',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '24px'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '700',
                color: '#e5e5e5'
              }}>
                Add Featured Product ({selectedProducts.length}/5)
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Search */}
            <div style={{ marginBottom: '16px' }}>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    background: '#333',
                    color: '#ccc',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer'
                    }}
                >
                    <Filter size={16} />
                    Filters {showFilters ? '−' : '+'}
                </button>
                
                {showFilters && (
                    <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '12px',
                    marginTop: '12px',
                    padding: '16px',
                    background: '#2a2a2a',
                    borderRadius: '8px'
                    }}>
                    <input
                        placeholder="Brand..."
                        value={searchFilters.brand}
                        onChange={(e) => setSearchFilters(prev => ({...prev, brand: e.target.value}))}
                        style={{
                        padding: '8px 12px',
                        border: '1px solid #444',
                        borderRadius: '6px',
                        background: colors.bgTertiary,
                        color: '#e5e5e5'
                        }}
                    />
                    <input
                        placeholder="Type..."
                        value={searchFilters.type}
                        onChange={(e) => setSearchFilters(prev => ({...prev, type: e.target.value}))}
                        style={{
                        padding: '8px 12px',
                        border: '1px solid #444',
                        borderRadius: '6px',
                        background: colors.bgTertiary,
                        color: '#e5e5e5'
                        }}
                    />
                    <select
                        value={searchFilters.minRating}
                        onChange={(e) => setSearchFilters(prev => ({...prev, minRating: e.target.value}))}
                        style={{
                        padding: '8px 12px',
                        border: '1px solid #444',
                        borderRadius: '6px',
                        background: colors.bgTertiary,
                        color: '#e5e5e5'
                        }}
                    >
                        <option value="0">Any Rating</option>
                        <option value="3">3+ Stars</option>
                        <option value="4">4+ Stars</option>
                        <option value="4.5">4.5+ Stars</option>
                    </select>
                    </div>
                )}
                </div>
            <div style={{
              position: 'relative',
              marginBottom: '20px'
            }}>
              <Search size={20} style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#888'
              }} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  paddingLeft: '48px',
                  paddingRight: '16px',
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  border: '2px solid #333',
                  borderRadius: '12px',
                  fontSize: '14px',
                  background: '#2a2a2a',
                  color: '#e5e5e5',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Products List */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'grid', gap: '12px' }}>
                {filteredProducts.map(product => (
                  <div
                    key={product.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px',
                      background: '#2a2a2a',
                      border: '1px solid #333',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => {
                      addProduct(product.id);
                      if (selectedProducts.length >= 4) {
                        setShowAddModal(false);
                      }
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#333';
                      e.currentTarget.style.borderColor = '#8b5cf6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#2a2a2a';
                      e.currentTarget.style.borderColor = '#333';
                    }}
                  >
                    {/* Product Image */}
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '8px',
                      background: '#333',
                      border: '1px solid #444',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}>
                      {product.coverImage ? (
                        <img
                          src={product.coverImage.imageUrl}
                          alt={product.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <Package size={20} style={{ color: '#666' }} />
                      )}
                    </div>

                    {/* Product Info */}
                    <div style={{ flex: 1 }}>
                      <h4 style={{
                        margin: '0 0 4px 0',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#e5e5e5'
                      }}>
                        {product.name}
                      </h4>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '12px',
                        color: '#888'
                      }}>
                        <span>{product.brand}</span>
                        <span>•</span>
                        <span style={{ textTransform: 'capitalize' }}>{product.type}</span>
                        {product.averageRating > 0 && (
                          <>
                            <span>•</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Star size={12} style={{ color: '#fbbf24' }} />
                              <span>{product.averageRating} ({product.reviewCount} reviews)</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Add Button */}
                    <button
                      style={{
                        padding: '8px 16px',
                        background: '#8b5cf6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Add
                    </button>
                  </div>
                ))}

                {filteredProducts.length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    padding: '32px',
                    color: '#888'
                  }}>
                    <Package size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
                    <p style={{ margin: 0, fontSize: '14px' }}>
                      {searchTerm ? 'No products found matching your search' : 'No more products available to add'}
                    </p>
                  </div>
                )}
              </div>
            </div>
            {hasMoreResults && (
            <div style={{ textAlign: 'center', padding: '16px' }}>
                <button
                onClick={() => searchProducts(searchTerm, searchPage + 1, false)}
                disabled={searchLoading}
                style={{
                    padding: '12px 24px',
                    background: '#8b5cf6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: searchLoading ? 'not-allowed' : 'pointer'
                }}
                >
                {searchLoading ? 'Loading...' : 'Load More Results'}
                </button>
            </div>
            )}

            {/* Modal Footer */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              paddingTop: '20px',
              borderTop: '1px solid #333'
            }}>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  padding: '12px 24px',
                  background: '#333',
                  color: '#ccc',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};